import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { KeyRound, Plus, Trash2, Copy, ShieldAlert, RefreshCw, Terminal, X, Eye } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";

const KNOWN_SCOPES = [
  { value: "leads:read",    label: "leads:read",    hint: "GET /api/leads (list, filter)" },
  { value: "leads:write",   label: "leads:write",   hint: "PATCH /api/leads/{id} (update status, owner)" },
  { value: "outreach:send", label: "outreach:send", hint: "POST /api/outreach — send email/SMS" },
  { value: "*",             label: "* (wildcard)",  hint: "Full admin equivalent — use only for trusted internal tools" },
];

const relTime = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - d) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function ApiTokensAdmin() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newRaw, setNewRaw] = useState(null);   // { token, raw_token }
  const [auditFor, setAuditFor] = useState(null); // { id, name }
  const [auditRows, setAuditRows] = useState([]);
  const [form, setForm] = useState({ name: "", scopes: ["leads:read"], role: "member", expires_at: "" });
  const [testerToken, setTesterToken] = useState("");
  const [testerBusy, setTesterBusy] = useState(false);
  const [testerResult, setTesterResult] = useState(null);

  const runTest = async (raw) => {
    const t = (raw || testerToken || "").trim();
    if (!t) return toast.error("Paste a token first.");
    setTesterBusy(true);
    setTesterResult(null);
    try {
      const r = await fetch(`${API}/leads?limit=3`, {
        method: "GET",
        headers: { "Authorization": `Bearer ${t}` },
      });
      const bodyText = await r.text();
      let bodyJson = null;
      try { bodyJson = JSON.parse(bodyText); } catch { /* not json */ }
      setTesterResult({
        status: r.status,
        ok: r.ok,
        count: (bodyJson?.leads || []).length,
        sample: (bodyJson?.leads || []).slice(0, 3).map((l) => ({ name: l.name, industry: l.industry, city: l.city })),
        error: !r.ok ? (bodyJson?.detail || bodyText.slice(0, 200)) : null,
      });
      if (r.ok) toast.success(`Token works · ${(bodyJson?.leads || []).length} leads returned`);
      else toast.error(`HTTP ${r.status} — ${(bodyJson?.detail || bodyText).slice(0, 100)}`);
    } catch (e) {
      setTesterResult({ status: 0, ok: false, error: String(e).slice(0, 200) });
      toast.error("Network error — check console.");
    } finally {
      setTesterBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/api-tokens");
      setTokens(data.tokens || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleScope = (s) => setForm((f) => ({
    ...f,
    scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
  }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("Name is required.");
    if (form.scopes.length === 0) return toast.error("Pick at least one scope.");
    setCreating(true);
    try {
      const body = { name: form.name.trim(), scopes: form.scopes, role: form.role };
      if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString();
      const { data } = await api.post("/admin/api-tokens", body);
      setNewRaw({ token: data.token, raw_token: data.raw_token });
      setShowModal(false);
      setForm({ name: "", scopes: ["leads:read"], role: "member", expires_at: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.name}"? Any partner using this token will immediately get 401. This can't be undone.`)) return;
    try {
      await api.delete(`/admin/api-tokens/${t.id}`);
      toast.success(`Revoked "${t.name}"`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const viewAudit = async (t) => {
    setAuditFor({ id: t.id, name: t.name });
    setAuditRows([]);
    try {
      const { data } = await api.get(`/admin/api-tokens/${t.id}/audit`);
      setAuditRows(data.audit || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyToClipboard = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copy failed — copy manually.");
    }
  };

  const curlSnippet = (raw) => `curl -H "Authorization: Bearer ${raw}" \\
  ${API}/leads`;

  return (
    <div className="max-w-5xl mx-auto p-8" data-testid="api-tokens-page">
      <Toaster position="top-right" richColors />

      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-neutral-900 flex items-center gap-3">
            <KeyRound className="w-7 h-7 text-[#E24E1B]" /> Partner API tokens
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl leading-relaxed">
            Long-lived, revocable, scoped Bearer tokens for 3rd-party integrations (Wix Symphony, Zapier,
            custom internal tools). Each token is <strong>only shown once at creation</strong> — copy it,
            paste it into the partner's onboarding, then it's gone from the UI.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="lf-btn-primary text-sm inline-flex items-center gap-2"
          data-testid="api-tokens-new-btn"
        >
          <Plus className="w-4 h-4" /> Issue token
        </button>
      </div>

      <div className="mb-6 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-3">
        <ShieldAlert className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
        <div>
          <strong>Security tips:</strong> give each partner a separate token (never share) · use the
          smallest scope that works (<code className="bg-white px-1">leads:read</code> is fine for most)
          · set an <code className="bg-white px-1">expires_at</code> for time-limited pilots ·
          rotate every 90 days. Revoked tokens 401 within seconds.
        </div>
      </div>

      {/* In-browser token tester — no terminal needed */}
      <div className="mb-6 bg-white border border-neutral-200 p-4" data-testid="api-tokens-tester">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3 flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5" /> Test any token here
        </div>
        <div className="flex gap-2">
          <input
            type="password"
            value={testerToken}
            onChange={(e) => setTesterToken(e.target.value)}
            placeholder="paste a cor_live_... token to verify"
            className="lf-input font-mono flex-1"
            data-testid="api-tokens-tester-input"
            autoComplete="off"
          />
          <button
            onClick={() => runTest()}
            disabled={testerBusy}
            className="lf-btn-primary text-sm whitespace-nowrap inline-flex items-center gap-2"
            data-testid="api-tokens-tester-btn"
          >
            <Terminal className="w-4 h-4" /> {testerBusy ? "Testing…" : "Send GET /api/leads?limit=3"}
          </button>
        </div>
        {testerResult && (
          <div className={`mt-3 p-3 border font-mono text-xs ${testerResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`} data-testid="api-tokens-tester-result">
            <div className="font-semibold mb-1">
              HTTP {testerResult.status} · {testerResult.ok ? `success — ${testerResult.count} lead${testerResult.count === 1 ? "" : "s"} returned` : "failed"}
            </div>
            {testerResult.ok ? (
              <div className="space-y-0.5">
                {testerResult.sample.map((s, i) => (
                  <div key={i}>· {s.name} — {s.industry} — {s.city}</div>
                ))}
              </div>
            ) : (
              <div>{testerResult.error}</div>
            )}
          </div>
        )}
        <p className="text-xs text-neutral-500 mt-2">Hits your own <code>/api/leads?limit=3</code> with the token — same request Symphony will make. Every test also lands in that token's audit log.</p>
      </div>

      {/* Tokens table */}
      <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="api-tokens-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
            {tokens.length} token{tokens.length === 1 ? "" : "s"}
          </div>
          <button onClick={load} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <table className="lf-grid">
          <thead>
            <tr>
              <th>Name</th>
              <th>Token</th>
              <th>Scopes</th>
              <th>Role</th>
              <th>Last used</th>
              <th className="text-right">Requests</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokens.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center text-neutral-500 py-10">
                No tokens issued yet. Click <strong>Issue token</strong> to create one for Wix Symphony.
              </td></tr>
            )}
            {tokens.map((t) => (
              <tr key={t.id} data-testid={`api-token-row-${t.id}`}>
                <td className="font-semibold">{t.name}</td>
                <td className="font-mono text-xs">cor_live_…{t.last4}</td>
                <td className="text-xs">
                  {(t.scopes || []).map((s) => (
                    <span key={s} className="inline-block bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 mr-1 font-mono text-[10px]">{s}</span>
                  ))}
                </td>
                <td className="text-xs">{t.role}</td>
                <td className="text-xs text-neutral-600">{relTime(t.last_used_at)}</td>
                <td className="text-right font-mono text-xs">{t.request_count || 0}</td>
                <td>
                  {t.revoked_at ? (
                    <span className="text-xs font-semibold text-rose-700">Revoked</span>
                  ) : t.expires_at && new Date(t.expires_at) < new Date() ? (
                    <span className="text-xs font-semibold text-amber-700">Expired</span>
                  ) : (
                    <span className="pill pill-interested text-[10px]">Active</span>
                  )}
                </td>
                <td className="text-right whitespace-nowrap">
                  <button
                    onClick={() => viewAudit(t)}
                    className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1 mr-2"
                    data-testid={`api-token-audit-${t.id}`}
                    title="View recent audit log"
                  >
                    <Eye className="w-3.5 h-3.5" /> Audit
                  </button>
                  {!t.revoked_at && (
                    <button
                      onClick={() => revoke(t)}
                      className="text-xs text-rose-600 hover:text-rose-800 inline-flex items-center gap-1"
                      data-testid={`api-token-revoke-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={create}
            className="bg-white w-full max-w-lg border border-neutral-300 shadow-2xl"
            data-testid="api-tokens-modal"
          >
            <div className="bg-[#0A192F] text-white px-5 py-3 flex items-center justify-between">
              <span className="font-bold">Issue partner API token</span>
              <button type="button" onClick={() => setShowModal(false)} className="hover:opacity-70"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block">
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Name</div>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Wix Symphony · Production"
                  className="lf-input w-full"
                  data-testid="api-tokens-name-input"
                  autoFocus
                />
              </label>

              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Scopes</div>
                <div className="space-y-1.5">
                  {KNOWN_SCOPES.map((s) => (
                    <label key={s.value} className="flex items-start gap-2 cursor-pointer hover:bg-neutral-50 p-2 border border-neutral-200">
                      <input
                        type="checkbox"
                        checked={form.scopes.includes(s.value)}
                        onChange={() => toggleScope(s.value)}
                        className="w-4 h-4 mt-0.5"
                        data-testid={`api-tokens-scope-${s.value}`}
                      />
                      <div>
                        <div className="text-sm font-mono font-semibold">{s.label}</div>
                        <div className="text-xs text-neutral-500">{s.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Role</div>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="lf-input w-full"
                    data-testid="api-tokens-role-select"
                  >
                    <option value="member">member (recommended)</option>
                    <option value="admin">admin (dangerous)</option>
                  </select>
                </label>
                <label className="block">
                  <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Expires (optional)</div>
                  <input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="lf-input w-full"
                    data-testid="api-tokens-expires-input"
                  />
                </label>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
              <button type="button" onClick={() => setShowModal(false)} className="lf-btn-outline text-sm">Cancel</button>
              <button type="submit" disabled={creating} className="lf-btn-primary text-sm" data-testid="api-tokens-create-submit">
                {creating ? "Creating…" : "Create token"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Show-once raw token modal */}
      {newRaw && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setNewRaw(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-2xl border border-[#E24E1B] shadow-2xl"
            data-testid="api-tokens-reveal-modal"
          >
            <div className="bg-[#E24E1B] text-white px-5 py-3 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              <span className="font-bold">Copy this token NOW — it will not be shown again</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Token</div>
                <div className="flex gap-2">
                  <code className="flex-1 font-mono text-xs bg-neutral-50 border border-neutral-200 p-3 break-all" data-testid="api-tokens-reveal-raw">
                    {newRaw.raw_token}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newRaw.raw_token, "Token copied to clipboard")}
                    className="lf-btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap"
                    data-testid="api-tokens-copy-btn"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5 flex items-center gap-1.5">
                  <Terminal className="w-3.5 h-3.5" /> Test cURL — paste into Symphony's onboarding
                </div>
                <div className="flex gap-2">
                  <pre className="flex-1 font-mono text-[11px] bg-[#0A192F] text-white p-3 overflow-x-auto whitespace-pre" data-testid="api-tokens-curl-snippet">
{curlSnippet(newRaw.raw_token)}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(curlSnippet(newRaw.raw_token), "cURL copied")}
                    className="lf-btn-outline text-sm inline-flex items-center gap-2 whitespace-nowrap self-start"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
              </div>

              <div className="border-t border-neutral-200 pt-3">
                <button
                  onClick={() => { setTesterToken(newRaw.raw_token); runTest(newRaw.raw_token); }}
                  disabled={testerBusy}
                  className="w-full lf-btn-primary text-sm inline-flex items-center justify-center gap-2"
                  data-testid="api-tokens-reveal-test-btn"
                >
                  <Terminal className="w-4 h-4" /> {testerBusy ? "Testing…" : "Test this token now (no terminal needed)"}
                </button>
                {testerResult && (
                  <div className={`mt-3 p-3 border font-mono text-xs ${testerResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}>
                    <div className="font-semibold mb-1">
                      HTTP {testerResult.status} · {testerResult.ok ? `success — ${testerResult.count} lead${testerResult.count === 1 ? "" : "s"} returned` : "failed"}
                    </div>
                    {testerResult.ok ? testerResult.sample.map((s, i) => <div key={i}>· {s.name}</div>) : <div>{testerResult.error}</div>}
                  </div>
                )}
              </div>

              <div className="text-xs text-neutral-500 leading-relaxed">
                <strong>Scopes granted:</strong> {(newRaw.token.scopes || []).join(", ")}
                {" · "}<strong>Role:</strong> {newRaw.token.role}
                {newRaw.token.expires_at && <> · <strong>Expires:</strong> {new Date(newRaw.token.expires_at).toLocaleString()}</>}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-200 flex justify-end">
              <button onClick={() => setNewRaw(null)} className="lf-btn-primary text-sm" data-testid="api-tokens-reveal-done">
                I've saved it — done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit log modal */}
      {auditFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAuditFor(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-3xl border border-neutral-300 shadow-2xl"
            data-testid="api-tokens-audit-modal"
          >
            <div className="bg-[#0A192F] text-white px-5 py-3 flex items-center justify-between">
              <span className="font-bold">Recent activity · {auditFor.name}</span>
              <button onClick={() => setAuditFor(null)} className="hover:opacity-70"><X className="w-4 h-4" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="lf-grid">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Method</th>
                    <th>Path</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-neutral-500 py-10">No requests recorded yet.</td></tr>
                  )}
                  {auditRows.map((a) => (
                    <tr key={a.id}>
                      <td className="text-xs whitespace-nowrap">{new Date(a.at).toLocaleString()}</td>
                      <td className="font-mono text-xs">{a.method}</td>
                      <td className="font-mono text-xs truncate max-w-md">{a.path}</td>
                      <td className="font-mono text-xs">{a.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
